import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { ConversationActions } from "@/components/feature/ChatHistory/ConversationActions"
import { MainLayout } from "@/components/layout/MainLayout/MainLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  CONVERSATION_TITLE_MAX_LENGTH,
  CONVERSATIONS_LOAD_ERROR_TOAST_ID,
} from "@/constants/UiConstants"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useCreateChatConversation,
  useGetChatConversations,
  useRenameChatConversation,
} from "@/services/api/chatConversationService"
import { isLimitError } from "@/utils/limitErrors"
import { Loader2, PlusIcon } from "lucide-react"
import React from "react"
import { useNavigate } from "react-router"

const ChatHistoryPage: React.FC = () => {
  const { translate } = useTranslation()
  const navigate = useNavigate()

  const { data, isPending, isError, error } = useGetChatConversations(1, 100)

  // When the backend returns 403, the feature flag is disabled.
  // The interceptor triggers a flag refresh; ChatHistoryRoute will
  // swap this page for ErrorPage once the refresh lands.
  const isFeatureDisabled =
    isError &&
    (error as { response?: { status?: number } })?.response?.status === 403

  const createConversation = useCreateChatConversation()
  const renameConversation = useRenameChatConversation()
  const [editingConversationId, setEditingConversationId] = React.useState<
    string | null
  >(null)
  const [editingTitle, setEditingTitle] = React.useState("")
  const [isRenameSubmitting, setIsRenameSubmitting] = React.useState(false)
  const editInputRef = React.useRef<HTMLInputElement | null>(null)
  const isRenameSubmittingRef = React.useRef(false)

  const focusAndSelectEditInput = React.useCallback(() => {
    const input = editInputRef.current
    if (!input) {
      return
    }

    input.focus()
    input.setSelectionRange(0, input.value.length)
  }, [])

  React.useEffect(() => {
    if (isError && !isFeatureDisabled) {
      toast.error(translate("AI_CHAT/HISTORY/LIST_ERROR"), {
        id: CONVERSATIONS_LOAD_ERROR_TOAST_ID,
      })
    }
  }, [isError, isFeatureDisabled, translate])

  React.useEffect(() => {
    if (!editingConversationId) {
      return
    }

    const rafId = window.requestAnimationFrame(() => {
      focusAndSelectEditInput()
    })
    const timeoutId = window.setTimeout(() => {
      focusAndSelectEditInput()
    }, 0)

    return () => {
      window.cancelAnimationFrame(rafId)
      window.clearTimeout(timeoutId)
    }
  }, [editingConversationId, focusAndSelectEditInput])

  const handleStartNewChat = async () => {
    if (createConversation.isPending) {
      return
    }

    try {
      const conversation = await createConversation.mutateAsync({})
      navigate(`/chat?conversation=${conversation.id}`)
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("AI_CHAT/HISTORY/CREATE_ERROR"))
      }
    }
  }

  const startInlineRename = (conversationId: string, title: string) => {
    setEditingConversationId(conversationId)
    setEditingTitle(title)
  }

  const cancelInlineRename = React.useCallback(() => {
    if (isRenameSubmittingRef.current) {
      return
    }

    setEditingConversationId(null)
    setEditingTitle("")
  }, [])

  React.useEffect(() => {
    if (!editingConversationId) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        return
      }

      if (editInputRef.current?.contains(target)) {
        return
      }

      cancelInlineRename()
    }

    document.addEventListener("mousedown", handlePointerDown, true)
    document.addEventListener("touchstart", handlePointerDown, true)

    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true)
      document.removeEventListener("touchstart", handlePointerDown, true)
    }
  }, [editingConversationId, cancelInlineRename])

  const saveInlineRename = async (
    conversationId: string,
    currentTitle: string,
  ) => {
    if (isRenameSubmittingRef.current) {
      return
    }

    const trimmedTitle = editingTitle.trim()
    if (!trimmedTitle) {
      toast.error(translate("AI_CHAT/HISTORY/RENAME_EMPTY_ERROR"))
      return
    }

    if (trimmedTitle === currentTitle.trim()) {
      cancelInlineRename()
      return
    }

    try {
      isRenameSubmittingRef.current = true
      setIsRenameSubmitting(true)
      await renameConversation.mutateAsync({
        conversationId,
        payload: { title: trimmedTitle },
      })
      toast.success(translate("AI_CHAT/HISTORY/RENAME_SUCCESS"))
      setEditingConversationId(null)
      setEditingTitle("")
    } catch (error) {
      if (!isLimitError(error)) {
        toast.error(translate("AI_CHAT/HISTORY/RENAME_ERROR"))
      }
    } finally {
      isRenameSubmittingRef.current = false
      setIsRenameSubmitting(false)
    }
  }

  return (
    <MainLayout
      titleInHeader
      title={translate("AI_CHAT/HISTORY/PAGE_TITLE")}
      contentCenter={
        <div data-agent-target="chat.history" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={handleStartNewChat}
              disabled={createConversation.isPending}
              data-testid="ai-chat-new-conv-button"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              {translate("AI_CHAT/NEW_CONVERSATION")}
            </Button>
          </div>

          {isPending ? (
            <div className="rounded-md border border-border p-4 text-muted-foreground">
              {translate("AI_CHAT/HISTORY/LOADING")}
            </div>
          ) : null}

          {!isPending && isError && !isFeatureDisabled ? (
            <div className="rounded-md border border-border p-4 text-muted-foreground">
              {translate("AI_CHAT/HISTORY/LIST_ERROR")}
            </div>
          ) : null}

          {!isPending &&
          !isError &&
          (data?.conversations?.length || 0) === 0 ? (
            <div className="rounded-md border border-border p-4 text-muted-foreground">
              {translate("AI_CHAT/HISTORY/EMPTY")}
            </div>
          ) : null}

          {!isPending && !isError && (data?.conversations?.length || 0) > 0 ? (
            <ul
              data-testid="chat-history-list"
              className="divide-y divide-border rounded-md border border-border"
            >
              {data?.conversations.map((conversation) => {
                const isEditing = editingConversationId === conversation.id

                return (
                  <li
                    key={conversation.id}
                    className="group/item flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40"
                  >
                    {isEditing ? (
                      <div className="min-w-0 flex-1">
                        <div className="relative">
                          <Input
                            ref={editInputRef}
                            value={editingTitle}
                            onChange={(event) =>
                              setEditingTitle(event.target.value)
                            }
                            onBlur={cancelInlineRename}
                            onFocus={() => {
                              focusAndSelectEditInput()
                              window.setTimeout(() => {
                                focusAndSelectEditInput()
                              }, 0)
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault()
                                if (isRenameSubmittingRef.current) {
                                  return
                                }
                                void saveInlineRename(
                                  conversation.id,
                                  conversation.title,
                                )
                              }
                              if (event.key === "Escape") {
                                event.preventDefault()
                                cancelInlineRename()
                              }
                            }}
                            className="h-9 border-border bg-background px-3 pr-9"
                            disabled={isRenameSubmitting}
                            maxLength={CONVERSATION_TITLE_MAX_LENGTH}
                            autoFocus
                          />
                          {isRenameSubmitting ? (
                            <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        title={conversation.title}
                        onClick={() =>
                          navigate(`/chat?conversation=${conversation.id}`)
                        }
                      >
                        <Typography
                          variant="textS"
                          className="truncate text-card-foreground"
                        >
                          {conversation.title}
                        </Typography>
                        <Typography variant="textXS" className="text-grey5">
                          {conversation.updatedAt.toLocaleString()}
                        </Typography>
                      </button>
                    )}

                    {!isEditing ? (
                      <ConversationActions
                        showOnHover
                        conversationId={conversation.id}
                        title={conversation.title}
                        onRenameInline={() =>
                          startInlineRename(conversation.id, conversation.title)
                        }
                      />
                    ) : null}
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      }
    />
  )
}

export default ChatHistoryPage
